import { FC, RefObject, useEffect, useMemo, useState } from 'react';
import { Button, Typography } from '@material-ui/core';
import { PublicKey } from '@solana/web3.js';
import { WalletMultiButton } from '@solana/wallet-adapter-material-ui';
import confetti from 'canvas-confetti';

import { Entrant, Raffle } from '../../../../lib/types';
import useCommonStyles from '../../../../assets/styles';
import { useStyles } from './styles';

//reveal winners
import { SYSVAR_RECENT_BLOCKHASHES_PUBKEY } from '@solana/web3.js';
import { txHandler } from '../../../../lib/anchorUtils';
import { useProgramApis } from '../../../../hooks/useProgramApis';
import { useRafflesStore } from '../../../../hooks/useRafflesStore';

import ClaimButton from '../../../../components/ClaimButton';
import { useParams } from 'react-router';
interface EndedRaffleActionsSectionProps {
  raffle: Raffle;
  claimPrize: (prizeIndex: number, ticketIndex: number) => Promise<void>;
  userPubkey: PublicKey | undefined;
  entrant: Entrant | undefined;
  entrantWinningTickets: { prizeIndex: number; ticketIndex: number }[];
  scrollRef: RefObject<HTMLDivElement>;
  publicKey: PublicKey | null;
  winningTickets: number[];
}

const EndedRaffleActionSection: FC<EndedRaffleActionsSectionProps> = ({
  raffle,
  userPubkey,
  entrant,
  entrantWinningTickets,
  claimPrize,
  winningTickets,
  publicKey,
  scrollRef,
}) => {
  const classes = { ...useCommonStyles(), ...useStyles() };

  const noPrizes = useMemo(() => raffle.prizes.length === 0, [raffle]);
  const winnersRevealed = useMemo(() => !!raffle.randomness, [raffle]);
  const userIsConnected = useMemo(() => !!userPubkey, [userPubkey]);
  const userHasParticipated = useMemo(
    () => (entrant?.tickets.length || 0) > 0,
    [entrant]
  );
  //reveal winner
  const [currentRaffle, setCurrentRaffle] = useState<Raffle>();
  const { id: raffleId } = useParams<{ id: string }>();
  const { raffles, updateRaffleById } = useRafflesStore();
  const { draffleClient } = useProgramApis();
  const [winnerKey, setWinnerKey] = useState('Retracted');

  useEffect(() => {
    const raffle = raffles.get(raffleId);
    if (raffle) setCurrentRaffle(raffle);
  }, [raffles, raffleId]);

  const revealWinners = async () => {
    await txHandler(
      () =>
        draffleClient.rpc.revealWinners({
          accounts: {
            raffle: raffleId,
            recentBlockhashes: SYSVAR_RECENT_BLOCKHASHES_PUBKEY,
          },
        }),
      'Raffle revealed'
    );
  };

  useEffect(() => {
    if (entrantWinningTickets.length === 0) return;
    const confettiTimerId = makeConfetti();
    return () => clearInterval(confettiTimerId);
  }, [entrantWinningTickets.length]);

  // console.log(winningTickets, 'wt')

  const ticketIndex = winningTickets[0];
  const isWon = entrantWinningTickets.some(
    (entrantWinningTicket) => entrantWinningTicket.ticketIndex === ticketIndex
  );

  useEffect(() => {
    // console.log(ticketIndex, 'winning ticket');
    // console.log(raffle.entrants, 'raffle');
    Array.from(raffle.entrants.values()).map((item: any) => {
      // console.log(item.tickets, 'ITEM')
      if (item.tickets.includes(ticketIndex))
        setWinnerKey(item.publicKey.toBase58());
    });
  }, [ticketIndex]);

  if (noPrizes) return <>No prizes to reveal!</>;

  if (!winnersRevealed)
    return (
      <div className="flex items-center justify-between w-full">
        <p
          className={
            'GTWalsheimProBold text-brand_secondary tracking-wider text-xs'
          }
        >
          Winners will be announced soon.
        </p>
        {userIsConnected && (
          <button
            onClick={(e) => revealWinners()}
            className="GTWalsheimProBold bg-brand_smooth_yellow text-xs text-brand_smooth_gray tracking-wider capitalize px-6 py-1 primary_font"
          >
            Reveal Winner
          </button>
        )}
      </div>
    );

  if (!userIsConnected)
    return (
      <div className="flex items-center">
        {/* <h3 className={'GTWalsheimProBold text-brand_secondary tracking-wider text-sm'}>
          WINNERS HAVE BEEN ANNOUNCED.
        </h3> */}
        <p className='GTWalsheimProBold text-xs lg:text-sm text-brand_secondary'>CONNECT YOUR WALLET TO CHECK IF YOU HAVE WON ANY PRIZES!</p>
        {/* <WalletMultiButton
        >
          Connect to reveal
        </WalletMultiButton> */}
      </div>
    );

  if (!userHasParticipated || entrantWinningTickets.length === 0)
    return (
      <div className="w-full flex">
        <h3
          className={
            'GTWalsheimProBold text-brand_accent tracking-wider text-xs pr-2'
          }
        >
          The Winner Is:
        </h3>
        <p className="GTWalsheimProBold text-xs md:text-sm md:hidden  text-brand_accent">
          {winnerKey?.charAt(0)}
          {winnerKey?.charAt(1)}
          {winnerKey?.charAt(2)}
          {winnerKey?.charAt(3)}
        </p>
        <p className="GTWalsheimProBold text-xs md:text-sm hidden md:block  text-brand_accent">
          {winnerKey}
        </p>
      </div>
    );

  // if (entrantWinningTickets.length === 0)
  //   return (
  // <div>
  //   <Typography variant="h3" className={"text-brand_secondary tracking-wider text-sm"}>
  //     Oh no! No prize.
  //   </Typography>
  //   <p>Better luck next time!</p>
  // </div>
  //   );

  return (
    <div className="flex flex-wrap items-center">
      <h3
        className={'text-brand_secondary tracking-wider text-sm pr-3'}
      >
        Hurray, you won!
      </h3>
      <div>
        {isWon ||
        (raffle.entrants.size === 0 &&
          raffle.creator.toBase58() === publicKey?.toBase58()) ? (
          <div className={''}>
            <ClaimButton
              claimPrize={claimPrize}
              prize={raffle.prizes[0]}
              prizeIndex={0}
              ticketIndex={ticketIndex}
            />
          </div>
        ) : (
          <></>
        )}
      </div>
    </div>
  );
};

const makeConfetti = () => {
  const duration = 5 * 1000;
  const animationEnd = Date.now() + duration;
  const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

  function randomInRange(min: number, max: number) {
    return Math.random() * (max - min) + min;
  }

  let intervalId: ReturnType<typeof setInterval>;
  intervalId = setInterval(() => {
    const timeLeft = animationEnd - Date.now();

    if (timeLeft <= 0) {
      return clearInterval(intervalId);
    }

    var particleCount = 50 * (timeLeft / duration);
    // since particles fall down, start a bit higher than random
    confetti(
      Object.assign({}, defaults, {
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
      })
    );
    confetti(
      Object.assign({}, defaults, {
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
      })
    );
  }, 250);

  return intervalId;
};

export default EndedRaffleActionSection;
